import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SortingState, ColumnFiltersState } from "@tanstack/react-table";
import { toast } from "sonner";
import { Plus, Loader2, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore } from "@/app/store/authStore";
import { Link } from "react-router-dom";
import { PaginatedResponse, ListQueryParams } from "@/app/types/api";
import { Category, CategoryCreate, CategoryUpdate } from "@/app/types/category";
import { listCategories, createCategory, updateCategory, deleteCategory } from "@/app/services/categoryService";
import CategoryDataTable from "./components/CategoryDataTable";
import CategoryForm from "./components/CategoryForm";
import { columns as categoryColumns } from "./components/columns";
import { AuthState } from "@/app/store/authStore";
import { User } from "@/app/types/auth";

// Define selectors for auth store
const selectUser = (state: AuthState): User | null => state.user;
const selectLogoutAction = (state: AuthState): (() => void) => state.logoutAction;
const selectIsLoading = (state: AuthState): boolean => state.isLoading;

const CategoriesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  // Local State
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    pageCount: 0,
  });
  
  // Auth state
  const user = useAuthStore(selectUser);
  const logoutAction = useAuthStore(selectLogoutAction);
  const isAuthLoading = useAuthStore(selectIsLoading);
  
  // Get organization ID from user data
  const userOrgId = useMemo(() => user?.organization_id || user?.organization?.id, [user]);
  const isOrgAdmin = useMemo(() => user?.role?.name === 'org_admin', [user]);
  
  // Construct query parameters for the API
  const queryParams = useMemo<ListQueryParams>(() => {
    // Ensure skip is never NaN
    const pageIndex = typeof pagination.pageIndex === 'number' && !isNaN(pagination.pageIndex) 
      ? pagination.pageIndex 
      : 0;
    const pageSize = typeof pagination.pageSize === 'number' && !isNaN(pagination.pageSize) 
      ? pagination.pageSize 
      : 10;
    
    const params: ListQueryParams = {
      skip: pageIndex * pageSize,
      limit: pageSize,
      search_query: searchValue || undefined,
    };
    
    // Add sorting if defined
    if (sorting.length > 0) {
      params.sort_by = sorting[0].id;
      params.sort_dir = sorting[0].desc ? "desc" : "asc";
    }
    
    // Add filters if defined
    if (columnFilters.length > 0) {
      const filters: { [key: string]: any } = {};
      columnFilters.forEach((filter) => {
        filters[filter.id] = filter.value;
      });
      params.filters = JSON.stringify(filters);
    }
    
    return params;
  }, [pagination.pageIndex, pagination.pageSize, searchValue, sorting, columnFilters]);
  
  // Query to fetch categories
  const { 
    data: categoriesResponse = { items: [], total: 0 },
    isLoading: isCategoriesLoading, 
    isError: isCategoriesError,
    error: categoriesError,
    refetch: refetchCategories,
  } = useQuery<PaginatedResponse<Category>>({
    queryKey: ['categories', userOrgId, queryParams],
    queryFn: () => {
      if (!userOrgId) {
        throw new Error('No organization ID available');
      }
      return listCategories(queryParams, userOrgId);
    },
    enabled: !!userOrgId,
  });

  // Query to fetch all categories for the parent dropdown
  const { data: allCategoriesForDropdown = [] } = useQuery<Category[]>({ 
    queryKey: ['allCategories', userOrgId],
    queryFn: () => {
      if (!userOrgId) return [];
      // Fetch all categories with basic params for the dropdown
      const dropdownParams: ListQueryParams = { skip: 0, limit: 500, sort_by: 'name', sort_dir: 'asc' }; 
      return listCategories(dropdownParams, userOrgId).then(res => res.items);
    },
    enabled: !!userOrgId,
  });
  
  // Update pagination when total changes
  useEffect(() => {
    if (categoriesResponse) {
      setPagination(prev => ({
        ...prev,
        pageCount: Math.ceil(categoriesResponse.total / prev.pageSize),
      }));
    }
  }, [categoriesResponse]);

  // Setup event listeners for edit/delete actions from the table
  useEffect(() => {
    const handleEditCategory = (event: CustomEvent<Category>): void => {
      navigate(`/settings/categories/detail/${event.detail.id}`);
    };

    const handleViewCategory = (event: CustomEvent<Category>): void => {
      navigate(`/settings/categories/detail/${event.detail.id}`);
    };

    const handleDeleteCategory = (event: CustomEvent<Category>): void => {
      setCurrentCategory(event.detail);
      setIsDeleteDialogOpen(true);
    };

    document.addEventListener('edit-category', handleEditCategory as EventListener);
    document.addEventListener('view-category', handleViewCategory as EventListener);
    document.addEventListener('delete-category', handleDeleteCategory as EventListener);

    return () => {
      document.removeEventListener('edit-category', handleEditCategory as EventListener);
      document.removeEventListener('view-category', handleViewCategory as EventListener);
      document.removeEventListener('delete-category', handleDeleteCategory as EventListener);
    };
  }, [navigate]);
  
  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: CategoryCreate) => createCategory(data, userOrgId!),
    onSuccess: () => {
      toast.success('Category created successfully');
      queryClient.invalidateQueries({ queryKey: ['categories', userOrgId] });
      queryClient.invalidateQueries({ queryKey: ['allCategories', userOrgId] }); // Invalidate dropdown query
      setIsCreateFormOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: CategoryUpdate }) => updateCategory(id, data),
    onSuccess: () => {
      toast.success('Category updated successfully');
      queryClient.invalidateQueries({ queryKey: ['categories', userOrgId] });
      queryClient.invalidateQueries({ queryKey: ['allCategories', userOrgId] }); // Invalidate dropdown query
      setIsEditFormOpen(false);
      setCurrentCategory(null);
    },
    onError: (error) => {
      toast.error(`Failed to update category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['categories', userOrgId] });
      queryClient.invalidateQueries({ queryKey: ['allCategories', userOrgId] }); // Invalidate dropdown query
      setIsDeleteDialogOpen(false);
      setCurrentCategory(null);
    },
    onError: (error) => {
      toast.error(`Failed to delete category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  });
  
  // Event Handlers
  const handleCreateClick = (): void => {
    navigate('/settings/categories/detail/new');
  };

  const handleCreateSubmit = (formData: CategoryCreate): void => {
    createMutation.mutate(formData);
  };

  const handleEditSubmit = (formData: CategoryUpdate): void => {
    if (currentCategory) {
      updateMutation.mutate({ id: currentCategory.id, data: formData });
    }
  };

  const handleDeleteConfirm = (): void => {
    if (currentCategory) {
      deleteMutation.mutate(currentCategory.id);
    }
  };
  
  const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }): void => {
    setPagination(prev => ({
      ...prev,
      pageIndex: typeof newPagination.pageIndex === 'number' && !isNaN(newPagination.pageIndex) 
        ? newPagination.pageIndex 
        : 0,
      pageSize: typeof newPagination.pageSize === 'number' && !isNaN(newPagination.pageSize) 
        ? newPagination.pageSize 
        : 10,
    }));
  };
  
  // --- Render Logic ---
  
  // Show loading indicator if auth state is loading
  if (isAuthLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }
  
  // No organization assigned
  if (!isAuthLoading && !userOrgId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] space-y-4 text-center p-8">
        <AlertTriangle className="h-16 w-16 text-warning" />
        <h2 className="text-2xl font-semibold">No Organization Assigned</h2>
        <p className="max-w-md text-muted-foreground">
          You need to be assigned to an organization to manage categories.
          Please contact your administrator.
        </p>
        <div className="flex gap-4 mt-4">
          <Link to="/dashboard">
            <Button variant="outline">Go to Dashboard</Button>
          </Link>
          <Button onClick={logoutAction} variant="destructive">
            Logout
          </Button>
        </div>
      </div>
    );
  }
  
  // Handle error state
  if (isCategoriesError && !!userOrgId) {
    return (
      <div className="py-8 flex flex-col items-center justify-center space-y-4 text-center">
        <AlertTriangle className="h-12 w-12 text-destructive" />
        <p className="text-xl font-semibold text-destructive">Error Loading Categories</p>
        <p className="text-sm text-muted-foreground">
          {categoriesError instanceof Error ? categoriesError.message : 'An unknown error occurred.'}
        </p>
        <Button onClick={() => refetchCategories()} variant="outline">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Retry
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Categories</h1>
        {isOrgAdmin && (
          <Button onClick={handleCreateClick}>
            <Plus className="mr-2 h-4 w-4" /> Add Category
          </Button>
        )}
      </div>
      <p className="text-muted-foreground">
        Organize items or services into hierarchical categories.
      </p>
      
      {/* Categories Data Table */}
      <CategoryDataTable
        data={categoriesResponse}
        columns={categoryColumns(allCategoriesForDropdown)}
        isLoading={isCategoriesLoading}
        allCategories={allCategoriesForDropdown}
        pagination={pagination}
        pageCount={pagination.pageCount}
        sorting={sorting}
        columnFilters={columnFilters}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onPaginationChange={handlePaginationChange}
        onSortingChange={setSorting}
        onColumnFiltersChange={setColumnFilters}
      />
      
      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the category &quot;{currentCategory?.name}&quot; and any subcategories.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CategoriesPage; 